import axiosInstance from './axiosInstance';

export type Department = {
  departmentId: string;
  departmentName: string;
  departmentLocation: string;
};

export type Location = {
  isActive: boolean;
  locationId: string;
  name: string;
  address: {
    id: string;
    name: string;
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  departments: Department[];
};

export type Organization = {
  orgName: string;
  orgId: string;
  id: string;
  gsTin: string;
  name: string;
  email: string;
  contact: string;
  isActive: boolean;
  locations: Location[];
  departments: Department[];
  hasAMC: boolean;
  isThisLeased: boolean;
  parentOrgId?: string
};

export const organizationRepository = {
  //  GET all organizations
  getOrganizations: async (): Promise<Organization[]> => {
    const { data } = await axiosInstance.get<{
      data: Organization[];
      totalCount: number;
    }>('/api/organizations');

    return data.data;
  },

  //  GET organization by ID
  getOrganizationById: async (id: string): Promise<Organization> => {
    const { data } = await axiosInstance.get<Organization>(`/api/organizations/${id}`);
    return data;
  },

  //  DELETE organization
  deleteOrganization: async (id: string): Promise<void> => {
    await axiosInstance.delete(`/api/organizations/${id}`);
  },

  // GET leased organization by ID
  getLeasedOrganizationById: async (orgId: string): Promise<Organization> => {
    const { data } = await axiosInstance.get<Organization>(
      `/api/organizations/leased/${orgId}`
    );
    return data;
  },
  getNonLeasedOrganizations: async (): Promise<Organization[]> => {
    const { data } = await axiosInstance.get<{
      data: Organization[];
      totalCount: number;
    }>('/api/organizations', {
      params: {
        pageNumber: 1,
        pageSize: 100,
        isLeased: false, // ✅ KEY PART
      },
    });

    return data.data;
  },
  //  ADD branch
  addBranch: async (orgId: string, branch: any): Promise<void> => {
    await axiosInstance.post(`/api/organizations/${orgId}/locations`, branch);
  },

  //Delete branch
  deleteBranch: async (orgId: String, locationId: String): Promise<void> => {
    await axiosInstance.delete(`/api/organizations/${orgId}/locations/${locationId}`);
  },

  //Edit Organization Info
  updateOrganization: async (orgId: String, data: any): Promise<void> => {
    await axiosInstance.put(`/api/organizations/${orgId}`, data);
  },

  //org list export
  exportOrgList: async (): Promise<Blob> => {
    const response = await axiosInstance.get('/api/organizations/export', {
      params: {
        format: 'csv',
      },
      responseType: 'blob',
      headers: {
        Accept: 'application/octet-stream',
      },
    });
    return response.data;
  },

  addOrganization: async (org: {
    name: string;
    email: string;
    contact: string;
    status: string;
    gsTin: string;
  }): Promise<Organization> => {
    const { data } = await axiosInstance.post<Organization>('/api/organizations', org);
    return data;
  },

  // ADD department
  addDepartment: async (orgId: string, locationId: string, department: any): Promise<void> => {
    await axiosInstance.post(
      `/api/organizations/${orgId}/locations/${locationId}/departments`,
      department
    );
  },

  // DELETE department
  deleteDepartment: async (orgId: string, locationId: string, deptId: string): Promise<void> => {
    await axiosInstance.delete(
      `/api/organizations/${orgId}/locations/${locationId}/departments/${deptId}`
    );
  },

  // UPDATE department
  updateDepartment: async (
    orgId: string,
    locationId: string,
    deptId: string,
    department: any
  ): Promise<void> => {
    await axiosInstance.put(
      `/api/organizations/${orgId}/locations/${locationId}/departments/${deptId}`,
      department
    );
  },

  //Update branch/location
  updateBranch: async (orgId: string, locationId: string, branch: any): Promise<void> => {
    await axiosInstance.put(`/api/organizations/${orgId}/locations/${locationId}`, branch);
  },

  //pagination
  getPaginatedOrganizations: async (
    pageNumber: number,
    pageSize: number,
    status?: string,
    search?: string,
    appliedSearch?: string,

  ): Promise<{ data: Organization[]; totalCount: number }> => {
    try {
      const response = await axiosInstance.get(`/api/organizations`, {
        params: {
          pageNumber,
          pageSize,
          status,
          search,
          appliedSearch,

        }
      });

      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return { data: [], totalCount: 0 };
      }
      throw error;
    }
  },
};
